import { Button, Col, Form, Input, Layout, Row, Select, Table } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import AlertNotAllow from '../AlertNotAllow';
import translate from '../../i18n/translate';
import Confirm from '../Confirm/Confirm';
import { storeContext } from '../../context';
import api from '../../helpers/api';
import { isAuthorized } from '../../helpers/verifyStatusErrorResponse';

const { Content } = Layout;
const { Option } = Select;
const { TextArea } = Input;

const labelCol = {
  xs: 24,
  sm: 24,
  md: 8,
  lg: 8,
  xl: 6,
};

const labelColValue = {
  xs: 24,
  sm: 24,
  md: 16,
  lg: 16,
  xl: 12,
};

const types = [
  'secret',
  'environment',
  'settings',
  'tag'
];

const INIT_VALUE = { key_type: null, comments: '' };

export default function DeployExternalServicesSettings() {
  const store = React.useContext(storeContext);

  const [permission, setPermission] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [state, setState] = useState(INIT_VALUE);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const columns = [
    {
      title: "ID",
      dataIndex: 'id',
      key: 'id'
    },
    {
      title: translate('default.keyType'),
      dataIndex: 'key_type',
      key: 'key_type'
    },
    {
      title: translate('default.key'),
      dataIndex: 'key',
      key: 'key'
    },
    {
      title: translate('default.value'),
      dataIndex: 'value',
      key: 'value',
      render: (text, record, index) => record.key_type === 'secret' && text ? '*********************' : text
    },
    {
      title: '',
      align: 'right',
      key: 'action',
      width: 200,
      render: (text, record, index) => (
        <>
          <Button icon="edit" type="default" onClick={() => setState({ ...record, index })} htmlType="button" style={{ marginRight: 10 }} />
          <Button icon="delete" type="danger" onClick={() => Confirm({ func: () => remove(index) })} htmlType="button" />
        </>
      ),
    }
  ];

  function isDisabled() {
    return !state || !state.key || !state.key_type || !state.value;
  }

  async function remove(index) {
    try {
      setLoading(true);
      await api.delete(`/deploy_settings/${data[index].id}`);
      const temp = [...data];
      temp.splice(index, 1);
      setData(temp);
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized) {
        return setPermission(false);
      }
      store.error = { message: translate('default.errorOccurred') }
    } finally {
      setLoading(false);
    }
  }

  async function onAdd() {
    const model = {
      deploy_id: store.currentItem.id,
      key_type: state.key_type,
      key: state.key,
      value: state.value,
    }
    await api.post('/deploy_settings', model);
    setState(INIT_VALUE);
  }

  async function onUpdate() {
    const model = {
      id: state.id,
      deploy_id: store.currentItem.id,
      key: state.key,
      key_type: state.key_type,
      value: state.value,
    }
    await api.put(`/deploy_settings/${state.id}`, model);
    setState(INIT_VALUE);
  }

  async function onSubmit(event) {
    setLoadingButton(true);
    event.preventDefault();
    setLoading(true);
    try {
      if (state.id) {
        await onUpdate();
      } else {
        await onAdd();
      }
      // Recarregar a lista após operação bem-sucedida
      // Passar false para não setar permission como false se houver erro na listagem
      await list(false);
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized) {
        setPermission(false);
        return;
      }
      store.error = { message: translate('default.errorOccurred') }
    } finally {
      setLoading(false);
      setLoadingButton(false);
    }
  }

  const list = useCallback(async (shouldSetPermission = true) => {
    setLoading(true);
    try {
      const data = await api.get(`/deploy_settings/deploy/${store.currentItem.id}`);
      if (data.data && data.data.result) {
        return setData(data.data.result);
      }
      setData([]);
    } catch (error) {
      const authorized = isAuthorized(error);
      if (!authorized && shouldSetPermission) {
        return setPermission(false);
      }
      if (!authorized && !shouldSetPermission) {
        // Se não deve setar permission, apenas logar o erro
        console.error('Erro de autorização ao recarregar lista:', error);
        return;
      }
      store.error = { message: translate('default.errorOccurred') }
    } finally {
      setLoading(false);
    }
  }, [store.currentItem.id, store.error]);

  useEffect(() => {
    list();
  }, [list]);

  return (
    <Content className="padding-20">
      {
        permission ?
          <>
            <Form layout="vertical" onSubmit={onSubmit}>
              <Row gutter={16}>
                <Col {...labelCol}>
                  <Form.Item label={translate('default.key')} required>
                    <Input value={state.key}
                      onChange={(e) => {
                        const key = e.target.value;
                        setState(prevState => ({ ...prevState, key }));
                      }} />
                  </Form.Item>
                </Col>
                <Col {...labelCol}>
                  <Form.Item label={translate('default.keyType')} required>
                    <Select defaultValue={null}
                      value={state.key_type} style={{ width: '100%' }}
                      onChange={(key_type) => setState(prevState => ({ ...prevState, key_type }))} >
                      <Option key={-1} value={null}>{translate('default.select.keyType')}</Option>
                      {types.map(x => <Option key={x}>{x}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...labelColValue}>
                  <Form.Item label={translate('default.value')} required>
                    <Input value={state.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        setState(prevState => ({ ...prevState, value }));
                      }} />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Comentários">
                    <TextArea
                      rows={3}
                      value={state.comments}
                      onChange={(e) => {
                        const comments = e.target.value;
                        setState(prevState => ({ ...prevState, comments }));
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col>
                  <Form.Item>
                    <Button loading={loadingButton} type="primary" disabled={isDisabled()} htmlType="submit" >
                      {state.id ? translate('default.save') : translate('default.add')}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <Col>
              <Table
                loading={loading}
                size="small"
                rowKey={record => record.id}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys) => setSelectedRowKeys(keys),
                }}
                columns={columns}
                dataSource={data}
                pagination={false}
              />
            </Col>
          </>
          :
          <AlertNotAllow />
      }
    </Content>
  )
}